import { get, post, put, getBlob, postBlob, del } from '../../api/api.service';
import { buildOfferEmailBody, buildOfferEmailSubject } from './customer-offer-email.templates';

export const OFFER_STATUS_DRAFT = 0;
export const OFFER_STATUS_PENDING_SIGNATURE = 1;
export const OFFER_STATUS_ACCEPTED = 2;
export const OFFER_STATUS_EXPIRED = 3;
export const OFFER_STATUS_DECLINED = 4;
export const OFFER_STATUS_CANCELLED = 5;

export interface CustomerOfferListItem {
  id: number;
  offerNumber: string;
  signUpUserId: number;
  isDraft: boolean;
  offerStatus: number;
  isArchived: boolean;
  customer: {
    customerId: number;
    name: string;
    email: string;
    orgNo: string;
  };
  offeredServices: string;
  contactPerson: string;
  contactPhone: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  amount: number;
  currency: string;
  compensationType: string;
  createdDate: string;
}

export interface GetAllCustomerOffersResponse {
  statusCode: number;
  message: string;
  response: CustomerOfferListItem[];
  totalCount: number;
}

export const getAllCustomerOffersApi = async (): Promise<GetAllCustomerOffersResponse> => {
  return get('/customerofferv2/get-all');
};

export interface PreferredOfferLanguageResponse {
  statusCode: number;
  message: string;
  response: string;
  totalCount: number;
  numberOfPages: number;
}

// `locale` is the fallback to use if the freelancer has no saved preference
// yet; `response` is the actual preferred offer language.
export const getPreferredOfferLanguageApi = async (
  locale: string
): Promise<PreferredOfferLanguageResponse> => {
  return get('/customerofferv2/preferred-language', { locale });
};

export const getArchivedCustomerOffersApi = async (): Promise<GetAllCustomerOffersResponse> => {
  return get('/customerofferv2/get-archived');
};

export interface CreateCustomerOfferPayload {
  id?: number;
  customerId: number;
  language: string;
  offeredServices: string;
  assignmentPeriod: string;
  workDescription: string;
  specialTerms: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  hasNoticePeriod: boolean;
  noticePeriodDuration: string;
  assignmentDescription: string;
  compensationType: string;
  currency: string;
  amount: number;
  numberOfUnits: number;
  contactPerson: string;
  contactPhone: string;
  isTermsAccepted: boolean;
  isDraft: boolean;
  freelancerSignatureDate?: string;
  freelancerSignatureData?: string;
  freelancerNameClarification?: string;
}

export interface CreateCustomerOfferResponse {
  statusCode: number;
  message: string;
}

export const createCustomerOfferApi = async (
  payload: CreateCustomerOfferPayload
): Promise<CreateCustomerOfferResponse> => {
  const response = await post<CreateCustomerOfferResponse>(
    '/customerofferv2/create',
    payload
  );
  return response;
};

// Renders a PDF from unsaved form data — nothing is persisted. Used to show
// the sender a preview before they've saved/sent anything.
export interface PreviewOfferPdfPayload {
  id?: number;
  customerId: number;
  language: string;
  offeredServices: string;
  assignmentPeriod: string;
  workDescription: string;
  specialTerms: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  hasNoticePeriod: boolean;
  noticePeriodDuration: string;
  assignmentDescription: string;
  compensationType: string;
  currency: string;
  amount: number;
  numberOfUnits: number;
  contactPerson: string;
  contactPhone: string;
  isTermsAccepted: boolean;
  customerSignatureDate: string;
  customerSignatureData: string;
  customerNameClarification: string;
  freelancerSignatureDate: string;
  freelancerSignatureData: string;
  freelancerNameClarification: string;
  isDraft: boolean;
}

export const previewOfferPdfApi = async (
  payload: PreviewOfferPdfPayload,
  locale: string
): Promise<Blob> => {
  return postBlob('/customerofferv2/preview-pdf', payload, { locale });
};

export interface CustomerOfferDetail {
  id: number;
  offerNumber: string;
  signUpUserId: number;
  customer: {
    customerId: number;
    name: string;
    email: string;
    orgNo: string;
    address?: string;
    city?: string;
    zipCode?: string;
  };
  offeredServices: string;
  assignmentPeriod: string;
  workDescription: string;
  specialTerms: string;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  hasNoticePeriod: boolean;
  noticePeriodDuration: string;
  assignmentDescription: string;
  compensationType: string;
  numberOfUnits: number;
  currency: string;
  amount: number;
  contactPerson: string;
  contactPhone: string;
  isTermsAccepted: boolean;
  freelancerSignatureDate: string;
  freelancerSignatureData: string;
  freelancerNameClarification: string;
  offerStatus: number;
  offerStatusLabel: string;
}

export interface GetCustomerOfferByIdResponse {
  statusCode: number;
  message: string;
  response: CustomerOfferDetail;
}

export const getCustomerOfferByIdApi = async (id: number): Promise<GetCustomerOfferByIdResponse> => {
  return get(`/customerofferv2/${id}`);
};

export const downloadOfferPdfApi = async (id: number, locale: string): Promise<Blob> => {
  return getBlob(`/customerofferv2/${id}/download-pdf`, { locale });
};

export interface SendOfferEmailPayload {
  toEmails: string[];
  subject: string;
  body: string;
  attachPdf: boolean;
  locale: string;
}

export interface SendOfferEmailResponse {
  statusCode: number;
  message: string;
}

export const sendOfferEmailApi = async (
  id: number,
  payload: SendOfferEmailPayload
): Promise<SendOfferEmailResponse> => {
  return post(`/customerofferv2/${id}/send-email`, payload);
};

// Shared by the offers grid's "send" action and the create-offer wizard's
// "Send to Customer" button, so both build the exact same email
// subject/body and hit the exact same endpoint.
export interface OfferEmailSummary {
  customerName: string;
  customerEmail: string;
  offerNumber: string;
  offeredServices: string;
  startDate: string;
  endDate: string | null;
  noEndDate: boolean;
  amount: number;
  currency: string;
  compensationType: string;
}

// Draft / Pending Signature offers haven't been agreed to yet, so they go
// out with an empty subject/body and let the backend generate its own
// (sign-invitation) content. Only Accepted offers get the client-built
// subject + HTML template — body is otherwise ignored by the backend anyway.
export const sendOfferAgreementEmail = async (
  id: number,
  summary: OfferEmailSummary,
  senderName: string,
  locale: string,
  offerStatus: number
): Promise<SendOfferEmailResponse> => {
  if (offerStatus !== OFFER_STATUS_ACCEPTED) {
    return sendOfferEmailApi(id, {
      toEmails: [summary.customerEmail],
      subject: '',
      body: '',
      attachPdf: true,
      locale,
    });
  }

  const emailData = {
    customerName: summary.customerName,
    senderName,
    offerNumber: summary.offerNumber,
    offeredServices: summary.offeredServices,
    startDate: summary.startDate,
    endDate: summary.endDate,
    noEndDate: summary.noEndDate,
    amount: summary.amount,
    currency: summary.currency,
    compensationType: summary.compensationType,
    locale,
  };

  return sendOfferEmailApi(id, {
    toEmails: [summary.customerEmail],
    subject: buildOfferEmailSubject(emailData),
    body: buildOfferEmailBody(emailData),
    attachPdf: true,
    locale,
  });
};

export interface DeleteCustomerOfferResponse {
  statusCode: number;
  message: string;
}

export const deleteCustomerOfferApi = async (id: number): Promise<DeleteCustomerOfferResponse> => {
  return del(`/customerofferv2/${id}`);
};

export interface ArchiveCustomerOfferResponse {
  statusCode: number;
  message: string;
}

export const archiveCustomerOfferApi = async (id: number): Promise<ArchiveCustomerOfferResponse> => {
  return put(`/customerofferv2/${id}/archive`, {});
};

export const unarchiveCustomerOfferApi = async (id: number): Promise<ArchiveCustomerOfferResponse> => {
  return put(`/customerofferv2/${id}/unarchive`, {});
};
